/**
 * @file dialog-provider.js
 * @description Provider do sistema de diálogos (CRA)
 */

import React, { useCallback, useState } from "react"
import { DialogContext } from "../contexts/dialog-context.js"
import { DialogHandler } from "../components/dialog-handler.js"

/**
 * Provider do sistema de diálogos
 * @param {Object} props - Propriedades do componente
 * @param {React.ReactNode} props.children - Componentes filhos
 * @returns {React.ReactElement} Provider de diálogos
 */
export function DialogProvider({ children }) {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
  })
  console.log("[DEBUG] DialogProvider render - isOpen:", dialogState.isOpen, "title:", dialogState.title)

  /**
   * Função para abrir o dialog
   * @param {Object} config - Configuração do dialog
   */
  const openDialog = useCallback((config) => {
    setDialogState({
      isOpen: true,
      ...config,
    })
  }, [])

  /**
   * Função para fechar o dialog
   */
  const closeDialog = useCallback(() => {
    setDialogState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }, [])

  /**
   * Função para confirmar o dialog
   */
  const confirmDialog = useCallback(() => {
    if (dialogState.onConfirm) {
      dialogState.onConfirm()
    }
    closeDialog()
  }, [dialogState.onConfirm, closeDialog])

  /**
   * Função para cancelar o dialog
   */
  const cancelDialog = useCallback(() => {
    if (dialogState.onCancel) {
      dialogState.onCancel()
    }
    closeDialog()
  }, [dialogState.onCancel, closeDialog])

  // Funções de conveniência para diferentes tipos de dialog
  const showInfo = useCallback(
    (message, title = "Informação") => {
      openDialog({ title, message, status: "info" })
    },
    [openDialog]
  )

  const showSuccess = useCallback(
    (message, title = "Sucesso") => {
      openDialog({ title, message, status: "success" })
    },
    [openDialog]
  )

  const showWarning = useCallback(
    (message, title = "Atenção") => {
      openDialog({ title, message, status: "warning" })
    },
    [openDialog]
  )

  const showError = useCallback(
    (message, title = "Erro") => {
      openDialog({ title, message, status: "error" })
    },
    [openDialog]
  )

  const showConfirm = useCallback(
    (message, onConfirm, title = "Confirmação", onCancel) => {
      openDialog({
        title,
        message,
        status: "confirm",
        onConfirm,
        onCancel,
      })
    },
    [openDialog]
  )

  const value = {
    isOpen: dialogState.isOpen,
    title: dialogState.title,
    message: dialogState.message,
    status: dialogState.status,
    onConfirm: dialogState.onConfirm,
    onCancel: dialogState.onCancel,
    openDialog,
    closeDialog,
    confirmDialog,
    cancelDialog,
    showInfo,
    showSuccess,
    showWarning,
    showError,
    showConfirm,
  }

  return React.createElement(
    DialogContext.Provider,
    { value },
    children,
    React.createElement(DialogHandler, {
      open: dialogState.isOpen,
      onClose: closeDialog,
      onConfirm: confirmDialog,
      onCancel: cancelDialog,
      title: dialogState.title,
      message: dialogState.message,
      type: dialogState.status,
      showCancel: dialogState.showCancel || !!dialogState.onCancel,
    })
  )
}
